/**
 * Derivations for the RFQ list.
 *
 * The four-step bar (Uploaded, BOM, Costed, Quoted) is Home's
 * `homeRowProgress` (`src/lib/homeDashboard.ts`), so both pages agree.
 * The list screen's stage column is the seven journey steps
 * (`journeySteps`), drawn as one segment each.
 */
import type { IntakeFinding, IntakeSource, RfqLine, RfqRecord, RfqStatus } from "./rfqApi";
import { homeRowProgress } from "./homeDashboard";
import { JOURNEY_KEYS, journeySteps } from "./rfqJourney";
import { rfqDisplayStatus } from "./rfqEvidence";
import { flattenDemandLines, lineThumbUrl } from "./rfqUnderstanding";

export type RfqStepState = "done" | "next" | "todo";

export type RfqListBucket = "bom" | "cost" | "quoted" | "other";

export type RfqListChip = "all" | "bom" | "cost" | "quoted" | "nocustomer";

export type RfqListProgress = {
  steps: [RfqStepState, RfqStepState, RfqStepState, RfqStepState];
  /** Which of the four step names sits under the bar. */
  labelIndex: 0 | 1 | 2 | 3;
  bucket: RfqListBucket;
};

const BUCKET: Record<RfqListProgress["labelIndex"], RfqListBucket> = { 0: "bom", 1: "cost", 2: "other", 3: "quoted" };

/**
 * Progress for one list row.
 *
 * The label sits under the last finished step: the step before "next", or
 * Quoted once every step is done. A quote-like status with no file and no
 * parsed drawing stays on Uploaded (`rfqDisplayStatus`).
 */
export function rfqListProgress(rfq: RfqRecord): RfqListProgress {
  const { steps } = homeRowProgress(rfq);
  const next = steps.indexOf("next");
  const labelIndex = (next === -1 ? 3 : Math.max(0, next - 1)) as RfqListProgress["labelIndex"];
  return { steps, labelIndex, bucket: BUCKET[labelIndex] };
}

/** Hover text for the bar: each step, with a check once it is done. */
export function stepTooltip(steps: RfqStepState[], labels: string[]): string {
  return steps.map((state, index) => `${labels[index] ?? ""}${state === "done" ? " ✓" : ""}`).join("  ");
}

/** A customer is a stored name or email. A blank of both is the "No customer" segment. */
export function rfqHasCustomer(rfq: Pick<RfqRecord, "customer_name" | "customer_email">): boolean {
  return Boolean((rfq.customer_name ?? "").trim() || (rfq.customer_email ?? "").trim());
}

export type AssignableCustomer = {
  /** Lowercased email, or `name:` plus the lowercased name when there is no email. */
  key: string;
  name: string;
  email: string | null;
};

/**
 * Customers that already exist on an RFQ or in the local book.
 * One row per email (or per name when the email is missing). Nothing is invented.
 */
export function assignableCustomers(
  rfqs: Pick<RfqRecord, "customer_name" | "customer_email">[],
  book: { name: string; email: string | null }[] = [],
): AssignableCustomer[] {
  const map = new Map<string, AssignableCustomer>();
  const put = (nameRaw: string | null | undefined, emailRaw: string | null | undefined) => {
    const name = (nameRaw ?? "").trim();
    const email = (emailRaw ?? "").trim();
    if (!name && !email) return;
    const key = email ? email.toLowerCase() : `name:${name.toLowerCase()}`;
    const prev = map.get(key);
    if (!prev) {
      map.set(key, { key, name: name || email, email: email || null });
      return;
    }
    if (name && (!prev.name || prev.name.toLowerCase() === (prev.email ?? "").toLowerCase())) prev.name = name;
    if (!prev.email && email) prev.email = email;
  };
  for (const rfq of rfqs) put(rfq.customer_name, rfq.customer_email);
  for (const row of book) put(row.name, row.email);
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
}

export function countChips(rfqs: RfqRecord[]): Record<RfqListChip, number> {
  const counts: Record<RfqListChip, number> = { all: rfqs.length, bom: 0, cost: 0, quoted: 0, nocustomer: 0 };
  for (const rfq of rfqs) {
    const bucket = rfqListProgress(rfq).bucket;
    if (bucket === "bom" || bucket === "cost" || bucket === "quoted") counts[bucket] += 1;
    if (!rfqHasCustomer(rfq)) counts.nocustomer += 1;
  }
  return counts;
}

export function matchesChip(rfq: RfqRecord, chip: RfqListChip): boolean {
  if (chip === "all") return true;
  if (chip === "nocustomer") return !rfqHasCustomer(rfq);
  return rfqListProgress(rfq).bucket === chip;
}

/**
 * Amber dot, separate from the done/failed badge.
 *
 * A pending decision is "no mark", not attention. A failed read is the red
 * cross. What is left: a supplier ask still drafted or waiting, or an intake
 * finding on the line that is not a failed read (missing, uncertain, unread).
 */
export function lineNeedsAttention(line: RfqLine, findings: IntakeFinding[]): boolean {
  if (findings.some((finding) => finding.line_id === line.id && finding.kind !== "failed")) return true;
  const ask = line.supplier_ask?.status;
  return ask === "draft" || ask === "waiting";
}

export type PartMarkReason = "costed" | "decided" | "unreadable" | "skipped";

export type PartMark = {
  kind: "ok" | "bad";
  reason: PartMarkReason;
};

export type PartMarkContext = {
  findings?: IntakeFinding[];
  sources?: IntakeSource[];
};

function lineUnreadable(line: RfqLine, ctx: PartMarkContext): boolean {
  const findings = ctx.findings ?? [];
  if (findings.some((finding) => finding.line_id === line.id && finding.kind === "failed")) return true;
  const sources = ctx.sources ?? [];
  return sources.some(
    (source) => source.read_state === "failed" && (source.line_ids ?? []).includes(line.id),
  );
}

/**
 * Badge for one part thumbnail.
 *
 * Failure wins. An unreadable CAD/file (`IntakeFinding.kind === "failed"` on
 * this line, or an `IntakeSource` with `read_state === "failed"` whose
 * `line_ids` include it) is "unreadable". A `decision_status` of "skipped"
 * is the excluded line. Otherwise a finite `est_cost_try` is "costed" — the
 * strongest done signal — and a "decided" line with no cost is "decided".
 * Pending, unread, and a missing thumbnail or job id get no badge. There is
 * no geometry-missing field on the line.
 */
export function partMark(line: RfqLine, ctx: PartMarkContext = {}): PartMark | null {
  if (lineUnreadable(line, ctx)) return { kind: "bad", reason: "unreadable" };
  if (line.decision_status === "skipped") return { kind: "bad", reason: "skipped" };
  if (typeof line.est_cost_try === "number" && Number.isFinite(line.est_cost_try)) {
    return { kind: "ok", reason: "costed" };
  }
  if (line.decision_status === "decided") return { kind: "ok", reason: "decided" };
  return null;
}

export type PartSlot = {
  key: string;
  url: string | null;
  name: string;
  attention: boolean;
  mark: PartMark | null;
};

export const PART_PREVIEW_MAX = 8;

/** Count badge plus up to eight previews. The rest is the "+N" tail. */
export function partPreview(rfq: RfqRecord, max = PART_PREVIEW_MAX): {
  total: number;
  slots: PartSlot[];
  rest: number;
} {
  const findings = rfq.findings ?? [];
  const sources = rfq.sources ?? [];
  const parts = flattenDemandLines(rfq.lines ?? []);
  if (parts.length > 0) {
    const slots = parts.slice(0, max).map((line) => ({
      key: line.id,
      url: lineThumbUrl(line),
      name: (line.part_no || line.description || "").trim(),
      attention: lineNeedsAttention(line, findings),
      mark: partMark(line, { findings, sources }),
    }));
    return { total: parts.length, slots, rest: Math.max(0, parts.length - slots.length) };
  }
  const urls = (rfq.part_thumbs ?? []).map((url) => url.trim()).filter(Boolean);
  const total = rfq.part_count || rfq.line_count || urls.length;
  const slots = urls.slice(0, max).map((url, index) => ({
    key: `${url}-${index}`,
    url,
    name: "",
    attention: false,
    mark: null,
  }));
  return { total, slots, rest: Math.max(0, total - slots.length) };
}

/** Domain after @, lowercased. Empty, or an address with no domain, is null. */
export function emailDomain(email: string | null | undefined): string | null {
  const trimmed = (email ?? "").trim();
  const at = trimmed.lastIndexOf("@");
  if (at <= 0 || at === trimmed.length - 1) return null;
  const domain = trimmed.slice(at + 1).trim().toLowerCase();
  if (!domain || /\s/.test(domain)) return null;
  return domain;
}

/**
 * Customer column. A stored name wins. When the name is empty, the email
 * domain is a hint only — it is not a company field.
 */
export function customerLabel(rfq: Pick<RfqRecord, "customer_name" | "customer_email">): {
  text: string;
  hinted: boolean;
} {
  const name = (rfq.customer_name ?? "").trim();
  if (name) return { text: name, hinted: false };
  const domain = emailDomain(rfq.customer_email);
  if (domain) return { text: domain, hinted: true };
  return { text: "", hinted: false };
}

/** Quote column. Null when there is no finite total yet; the cell draws an em dash. */
export function formatQuote(
  amount: number | null | undefined,
  currency: string | null | undefined,
  locale: string,
): string | null {
  if (typeof amount !== "number" || !Number.isFinite(amount)) return null;
  const tag = locale === "tr" ? "tr-TR" : "en-US";
  const digits = Number.isInteger(amount) ? 0 : 2;
  const formatted = new Intl.NumberFormat(tag, {
    minimumFractionDigits: digits,
    maximumFractionDigits: 2,
  }).format(amount);
  const code = (currency ?? "").trim();
  return code ? `${formatted} ${code}` : formatted;
}

/** A due date is overdue once its UTC calendar day is before today's. */
export function isDueOverdue(iso: string | null | undefined, now = new Date()): boolean {
  if (!iso) return false;
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return false;
  const due = new Date(at);
  const dueDay = Date.UTC(due.getUTCFullYear(), due.getUTCMonth(), due.getUTCDate());
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return dueDay < today;
}

/** RFQ number, customer, email, assembly title, and part names. */
export function rfqMatchesQuery(rfq: RfqRecord, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const parts = flattenDemandLines(rfq.lines ?? []);
  const hay = [
    rfq.rfq_no,
    rfq.title,
    rfq.customer_name,
    rfq.customer_email,
    ...parts.flatMap((line) => [line.part_no, line.description]),
  ]
    .filter((value): value is string => Boolean(value && value.trim()))
    .join("\n")
    .toLowerCase();
  return hay.includes(needle);
}

/** Latest stamp we can read: the row's own edit, a sent quote, or a reply. */
export function lastActivityAt(rfq: RfqRecord): string | null {
  const stamps = [
    rfq.updated_at,
    rfq.sent_at,
    rfq.outcome_at,
    ...(rfq.customer_replies ?? []).map((reply) => reply.created_at),
  ];
  let best: number | null = null;
  let iso: string | null = null;
  for (const stamp of stamps) {
    if (!stamp) continue;
    const at = Date.parse(stamp);
    if (Number.isNaN(at)) continue;
    if (best == null || at > best) {
      best = at;
      iso = stamp;
    }
  }
  return iso;
}

export type SelectionState = "none" | "some" | "all";

/** Header checkbox: all visible rows, some of them, or none. */
export function selectionState(ids: string[], selected: ReadonlySet<string>): SelectionState {
  if (ids.length === 0) return "none";
  let hits = 0;
  for (const id of ids) if (selected.has(id)) hits += 1;
  if (hits === 0) return "none";
  if (hits === ids.length) return "all";
  return "some";
}

/** Select every visible id, or clear them when they are already all selected. */
export function toggleVisibleSelection(ids: string[], selected: ReadonlySet<string>): Set<string> {
  const next = new Set(selected);
  if (selectionState(ids, selected) === "all") {
    for (const id of ids) next.delete(id);
  } else {
    for (const id of ids) next.add(id);
  }
  return next;
}

export type RfqListSort = { key: "received" | "number" | "updated"; dir: "asc" | "desc" };

function rfqNumberOrder(a: RfqRecord, b: RfqRecord): number {
  return a.rfq_no.localeCompare(b.rfq_no, undefined, { numeric: true, sensitivity: "base" });
}

export function sortRfqs(rows: RfqRecord[], sort: RfqListSort): RfqRecord[] {
  const dir = sort.dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (sort.key === "number") return rfqNumberOrder(a, b) * dir;
    const stamp = sort.key === "updated" ? "updated_at" : "created_at";
    const am = Date.parse(a[stamp] ?? a.created_at ?? "") || 0;
    const bm = Date.parse(b[stamp] ?? b.created_at ?? "") || 0;
    if (am !== bm) return (am - bm) * dir;
    return rfqNumberOrder(a, b) * dir;
  });
}

/** Rows on one page of the list. */
export const RFQ_LIST_PAGE_SIZE = 10;

/** The date chip on the list. Calendar days, so a stamp a few milliseconds older still counts. */
export const RFQ_LIST_DATE_DAYS = 30;

const CLOSED_STATUS = new Set<RfqStatus>(["won", "lost", "cancelled"]);

function startOfLocalDay(date: Date): number {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next.getTime();
}

/**
 * Created (or, when that is missing, updated) inside the last `days` calendar
 * days, including today. A future stamp stays in — that is clock skew, not an
 * old RFQ. No stamp at all stays out: there is no date to filter on.
 */
export function rfqInDateWindow(rfq: RfqRecord, days: number, now = new Date()): boolean {
  const iso = rfq.created_at ?? rfq.updated_at ?? null;
  if (!iso) return false;
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return false;
  const diffDays = Math.floor((startOfLocalDay(now) - startOfLocalDay(new Date(at))) / 86_400_000);
  if (diffDays < 0) return true;
  return diffDays <= days;
}

/** A failed read, a blocking or missing finding, an unreadable line, or a supplier ask still open. */
export function rfqHasOpenCheck(rfq: RfqRecord): boolean {
  const sources = rfq.sources ?? [];
  if (sources.some((source) => source.read_state === "failed")) return true;
  const findings = rfq.findings ?? [];
  if (
    findings.some(
      (finding) =>
        finding.kind === "failed" ||
        finding.kind === "missing" ||
        finding.kind === "uncertain" ||
        finding.severity === "blocker",
    )
  ) {
    return true;
  }
  return flattenDemandLines(rfq.lines ?? []).some((line) => {
    if (lineNeedsAttention(line, findings)) return true;
    return partMark(line, { findings, sources })?.reason === "unreadable";
  });
}

/**
 * Needs action: stock is short, a file failed, or a draft / estimating RFQ
 * still has something to check. Ready, sent and waiting-for-customer are not
 * "needs action" unless stock is missing or a file failed to read. Closed
 * outcomes are not.
 */
export function rfqNeedsAction(rfq: RfqRecord): boolean {
  const status = rfqDisplayStatus(rfq);
  if (CLOSED_STATUS.has(status)) return false;
  if ((rfq.stock_missing_count ?? 0) > 0) return true;
  if ((rfq.sources ?? []).some((source) => source.read_state === "failed")) return true;
  if (status === "ready" || status === "sent" || status === "waiting_customer") return false;
  return rfqHasOpenCheck(rfq);
}

/** Awaiting approval: the quote is ready, or the desk is waiting on the customer. */
export function rfqAwaitsApproval(rfq: RfqRecord): boolean {
  const status = rfqDisplayStatus(rfq);
  return status === "ready" || status === "waiting_customer";
}

/** Closed: won, lost or cancelled. Sent stays open. */
export function rfqIsClosed(rfq: RfqRecord): boolean {
  return CLOSED_STATUS.has(rfqDisplayStatus(rfq));
}

export type RfqListTab = "all" | "action" | "approval" | "closed";

export function countTabs(rfqs: RfqRecord[]): Record<RfqListTab, number> {
  const counts: Record<RfqListTab, number> = { all: rfqs.length, action: 0, approval: 0, closed: 0 };
  for (const rfq of rfqs) {
    if (rfqNeedsAction(rfq)) counts.action += 1;
    if (rfqAwaitsApproval(rfq)) counts.approval += 1;
    if (rfqIsClosed(rfq)) counts.closed += 1;
  }
  return counts;
}

export function matchesTab(rfq: RfqRecord, tab: RfqListTab): boolean {
  if (tab === "all") return true;
  if (tab === "action") return rfqNeedsAction(rfq);
  if (tab === "approval") return rfqAwaitsApproval(rfq);
  return rfqIsClosed(rfq);
}

export type StageTone = "danger" | "warning" | "neutral";

export type StageCaption =
  | { tone: "danger"; kind: "stock"; count: number }
  | { tone: "danger"; kind: "lost" }
  | { tone: "warning"; kind: "check" }
  | { tone: "warning"; kind: "waiting" }
  | { tone: "neutral"; kind: "quoteReady" }
  | { tone: "neutral"; kind: "parameters" }
  | { tone: "neutral"; kind: "sent" }
  | { tone: "neutral"; kind: "won" }
  | { tone: "neutral"; kind: "cancelled" }
  | { tone: "neutral"; kind: "estimating" }
  | { tone: "neutral"; kind: "draft" };

/**
 * One line under the stage bar.
 *
 * Stock missing wins on an open RFQ — the count is `stock_missing_count`.
 * A closed outcome, a sent quote, and a ready quote name that status.
 * Otherwise a real check (failed file, finding, supplier ask) is "Check
 * needed". Parameters are set when the journey would call them done: a due
 * date, or a route op that already has params. Nothing here is invented.
 */
export function stageCaption(rfq: RfqRecord): StageCaption {
  const status = rfqDisplayStatus(rfq);
  if (status === "lost") return { tone: "danger", kind: "lost" };
  if (status === "won") return { tone: "neutral", kind: "won" };
  if (status === "cancelled") return { tone: "neutral", kind: "cancelled" };
  const missing = rfq.stock_missing_count ?? 0;
  if (missing > 0) return { tone: "danger", kind: "stock", count: missing };
  if (status === "sent") return { tone: "neutral", kind: "sent" };
  if (status === "ready") return { tone: "neutral", kind: "quoteReady" };
  if (status === "waiting_customer") return { tone: "warning", kind: "waiting" };
  if (rfqHasOpenCheck(rfq)) return { tone: "warning", kind: "check" };
  if (parametersAreSet(rfq)) return { tone: "neutral", kind: "parameters" };
  if (status === "estimating") return { tone: "neutral", kind: "estimating" };
  return { tone: "neutral", kind: "draft" };
}

function parametersAreSet(rfq: RfqRecord): boolean {
  const lines = flattenDemandLines(rfq.lines ?? []);
  if (lines.length === 0) return false;
  if (rfq.due_at) return true;
  return lines.some((line) =>
    (line.route_json ?? []).some((op) => {
      const params = op.params;
      return !!params && Object.keys(params).length > 0;
    }),
  );
}

/** Seven segments, one per journey step. Filled through the first step that is not done. */
export const STAGE_SEGMENT_COUNT = JOURNEY_KEYS.length;

export function stageFilled(rfq: RfqRecord, now = Date.now()): number {
  const steps = journeySteps(rfq, "en", { now });
  let filled = 0;
  for (const step of steps) {
    if (step.state === "done") {
      filled += 1;
      continue;
    }
    if (step.state === "current" || step.state === "blocked") filled += 1;
    break;
  }
  return Math.min(STAGE_SEGMENT_COUNT, Math.max(0, filled));
}

export type UpdatedWhen =
  | { kind: "none" }
  | { kind: "today" }
  | { kind: "yesterday" }
  | { kind: "days"; n: number };

/** Calendar-day age of a stamp. "2 days" matches the list, not "2 days ago". */
export function updatedWhen(iso: string | null | undefined, now = new Date()): UpdatedWhen {
  if (!iso) return { kind: "none" };
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return { kind: "none" };
  const diffDays = Math.floor((startOfLocalDay(now) - startOfLocalDay(new Date(at))) / 86_400_000);
  if (diffDays <= 0) return { kind: "today" };
  if (diffDays === 1) return { kind: "yesterday" };
  return { kind: "days", n: diffDays };
}

/**
 * Amount column. TRY (and a blank currency on `quote_total_try`) uses ₺.
 * Another code keeps its letters — an EUR total is not relabelled ₺.
 * Two fraction digits, the way the list draws money.
 */
export function formatListAmount(
  amount: number | null | undefined,
  currency: string | null | undefined,
  locale: string,
): string | null {
  if (typeof amount !== "number" || !Number.isFinite(amount)) return null;
  const tag = locale === "tr" ? "tr-TR" : "en-US";
  const formatted = new Intl.NumberFormat(tag, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
  const code = (currency ?? "").trim().toUpperCase();
  if (!code || code === "TRY") return `${formatted} ₺`;
  return `${formatted} ${code}`;
}

export function partHoverName(line: RfqLine): string {
  const partNo = (line.part_no ?? "").trim();
  const description = (line.description ?? "").trim();
  if (partNo && description && partNo !== description) return `${partNo}, ${description}`;
  return partNo || description;
}

/** Material plus the route's op labels. Null when the line has neither. */
export function partProcessLine(line: RfqLine, locale: "en" | "tr"): string | null {
  const material = (line.material ?? "").trim();
  const ops = (line.route_json ?? [])
    .map((op) => ((locale === "tr" ? op.label_tr || op.op : op.op) ?? "").trim())
    .filter(Boolean);
  const process = ops.join(" + ");
  if (material && process) return `${material}, ${process}`;
  return material || process || null;
}

export function partCostTry(line: RfqLine): number | null {
  return typeof line.est_cost_try === "number" && Number.isFinite(line.est_cost_try) ? line.est_cost_try : null;
}

/** How many loaded RFQs name this part number. Null when the line has no part number. */
export function usedInRfqCount(rfqs: readonly RfqRecord[], partNo: string | null | undefined): number | null {
  const key = (partNo ?? "").trim().toLowerCase();
  if (!key) return null;
  let count = 0;
  for (const rfq of rfqs) {
    const hit = flattenDemandLines(rfq.lines ?? []).some(
      (line) => (line.part_no ?? "").trim().toLowerCase() === key,
    );
    if (hit) count += 1;
  }
  return count;
}

/** A job the Documents page can open, from a line or an intake source. */
export function rfqDocumentJobId(rfq: RfqRecord): string | null {
  for (const line of flattenDemandLines(rfq.lines ?? [])) {
    const job = (line.source_job_id ?? "").trim();
    if (job) return job;
  }
  for (const source of rfq.sources ?? []) {
    const job = (source.source_job_id ?? "").trim();
    if (job) return job;
  }
  return null;
}

function csvCell(value: string): string {
  if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

/**
 * Selected rows as CSV. Only fields the record actually has — no uploader
 * column, because an RFQ does not store one.
 */
export function rfqsToCsv(rows: readonly RfqRecord[]): string {
  const header = ["rfq_no", "title", "company", "email", "status", "amount", "currency", "created_at", "updated_at"];
  const lines = [header.join(",")];
  for (const rfq of rows) {
    const amount =
      typeof rfq.quote_total_try === "number" && Number.isFinite(rfq.quote_total_try)
        ? String(rfq.quote_total_try)
        : "";
    lines.push(
      [
        rfq.rfq_no,
        rfq.title ?? "",
        (rfq.customer_name ?? "").trim(),
        (rfq.customer_email ?? "").trim(),
        rfq.status,
        amount,
        (rfq.quote_currency ?? "").trim(),
        rfq.created_at ?? "",
        rfq.updated_at ?? "",
      ]
        .map((value) => csvCell(value))
        .join(","),
    );
  }
  return lines.join("\n");
}
